import { useState } from 'react';
import { useCollection } from '@/hooks/useCollection';
import { createRow, updateRow, deleteRow, TableName } from '@/lib/api';
import ImageUpload from '@/components/base/ImageUpload';
import MultiImageUpload from '@/components/base/MultiImageUpload';

export interface FieldConfig {
  name: string;
  label: string;
  type: 'text' | 'textarea' | 'number' | 'url' | 'date' | 'time' | 'checkbox' | 'select' | 'category' | 'image' | 'images';
  required?: boolean;
  options?: { value: string; label: string }[];
  placeholder?: string;
  help?: string;
}

export interface ColumnConfig {
  key: string;
  label: string;
  type?: 'text' | 'image' | 'boolean' | 'date';
}

interface EntityManagerProps {
  table: TableName;
  fields: FieldConfig[];
  columns: ColumnConfig[];
  orderBy?: string;
  ascending?: boolean;
  searchKeys?: string[];
  addLabel?: string;
}

type FormState = Record<string, string | number | boolean | string[]>;

function toInitial(fields: FieldConfig[], row?: Record<string, unknown>): FormState {
  const state: FormState = {};
  fields.forEach((f) => {
    if (f.type === 'checkbox') {
      state[f.name] = row ? Boolean(row[f.name]) : false;
    } else if (f.type === 'number') {
      state[f.name] = row && row[f.name] != null ? Number(row[f.name]) : 0;
    } else if (f.type === 'time' && row && typeof row[f.name] === 'string') {
      state[f.name] = (row[f.name] as string).slice(0, 5);
    } else if (f.type === 'images') {
      const raw = row ? row[f.name] : null;
      state[f.name] = Array.isArray(raw) ? (raw as string[]) : [];
    } else {
      state[f.name] = row && row[f.name] != null ? String(row[f.name]) : '';
    }
  });
  return state;
}

export default function EntityManager({
  table,
  fields,
  columns,
  orderBy = 'created_at',
  ascending = false,
  searchKeys = [],
  addLabel = 'Add New',
}: EntityManagerProps) {
  const { rows, loading, error, reload } = useCollection<Record<string, unknown>>(
    table,
    orderBy,
    ascending,
  );
  const [search, setSearch] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>({});
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const hasPublish = fields.some((f) => f.name === 'is_published');

  const distinctOptions = (name: string): string[] => {
    const set = new Set<string>();
    rows.forEach((r) => {
      const v = r[name];
      if (typeof v === 'string' && v.trim()) set.add(v.trim());
    });
    return Array.from(set).sort();
  };

  const filtered = rows.filter((row) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return searchKeys.some((k) => String(row[k] ?? '').toLowerCase().includes(q));
  });

  const openAdd = () => {
    setEditingId(null);
    const initial = toInitial(fields);
    // Default new records to visible/published so they appear on the website immediately.
    if ('is_published' in initial) initial.is_published = true;
    setForm(initial);
    setFormError(null);
    setModalOpen(true);
  };

  const openEdit = (row: Record<string, unknown>) => {
    setEditingId(String(row.id));
    setForm(toInitial(fields, row));
    setFormError(null);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingId(null);
  };

  const handleSave = async () => {
    setSaving(true);
    setFormError(null);
    try {
      const payload: Record<string, unknown> = { ...form };
      // Convert empty strings to null for optional fields
      fields.forEach((f) => {
        if (f.type === 'images') {
          payload[f.name] = Array.isArray(payload[f.name]) ? payload[f.name] : [];
          return;
        }
        if (f.type !== 'checkbox' && f.type !== 'number' && payload[f.name] === '') {
          payload[f.name] = null;
        }
      });

      if (editingId) {
        await updateRow(table, editingId, payload);
      } else {
        await createRow(table, payload);
      }
      closeModal();
      reload();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleTogglePublish = async (row: Record<string, unknown>) => {
    setBusy(true);
    try {
      await updateRow(table, String(row.id), { is_published: !row.is_published });
      reload();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to update');
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    setBusy(true);
    try {
      await deleteRow(table, confirmDelete);
      setConfirmDelete(null);
      reload();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to delete');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search..."
          className="px-4 py-2.5 rounded-md text-sm border border-background-300 bg-white outline-none focus:ring-2 focus:ring-primary-300 w-full sm:w-72"
          aria-label="Search records"
        />
        <button type="button" onClick={openAdd} className="btn-primary whitespace-nowrap">
          <i className="ri-add-line" aria-hidden="true" />
          {addLabel}
        </button>
      </div>

      {formError && (
        <div className="mb-4 px-4 py-3 rounded-md bg-red-50 text-red-600 text-sm border border-red-200">
          {formError}
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div className="py-16 text-center text-foreground-500">
          <i className="ri-loader-4-line animate-spin text-2xl inline-block" aria-hidden="true" />
          <p className="mt-2 text-sm">Loading...</p>
        </div>
      ) : error ? (
        <div className="py-16 text-center">
          <p className="text-red-600 text-sm">{error}</p>
          <button type="button" onClick={reload} className="btn-outline mt-4">
            Retry
          </button>
        </div>
      ) : filtered.length === 0 ? (
        <div className="py-16 text-center bg-white rounded-lg border border-background-200">
          <i className="ri-inbox-line text-4xl text-foreground-300" aria-hidden="true" />
          <p className="mt-3 text-sm text-foreground-500">No records found</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-background-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-background-100 text-left text-xs uppercase tracking-wider text-foreground-500">
                  {columns.map((c) => (
                    <th key={c.key} className="px-4 py-3 font-label font-semibold">
                      {c.label}
                    </th>
                  ))}
                  <th className="px-4 py-3 font-label font-semibold text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((row) => (
                  <tr key={String(row.id)} className="border-t border-background-100 hover:bg-background-50">
                    {columns.map((c) => (
                      <td key={c.key} className="px-4 py-3 align-middle">
                        <Cell column={c} value={row[c.key]} />
                      </td>
                    ))}
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        {hasPublish && (
                          <button
                            type="button"
                            onClick={() => handleTogglePublish(row)}
                            disabled={busy}
                            title={row.is_published ? 'Unpublish' : 'Publish'}
                            className={`w-8 h-8 flex items-center justify-center rounded-md cursor-pointer transition-colors ${
                              row.is_published
                                ? 'text-accent-600 hover:bg-accent-100'
                                : 'text-foreground-400 hover:bg-background-100'
                            }`}
                          >
                            <i
                              className={row.is_published ? 'ri-eye-line' : 'ri-eye-off-line'}
                              aria-hidden="true"
                            />
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => openEdit(row)}
                          title="Edit"
                          className="w-8 h-8 flex items-center justify-center rounded-md text-primary-600 hover:bg-primary-100 cursor-pointer"
                        >
                          <i className="ri-edit-line" aria-hidden="true" />
                        </button>
                        <button
                          type="button"
                          onClick={() => setConfirmDelete(String(row.id))}
                          title="Delete"
                          className="w-8 h-8 flex items-center justify-center rounded-md text-red-500 hover:bg-red-50 cursor-pointer"
                        >
                          <i className="ri-delete-bin-line" aria-hidden="true" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Form Modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={closeModal} aria-hidden="true" />
          <div className="relative bg-white rounded-xl w-full max-w-2xl max-h-[90vh] flex flex-col shadow-2xl">
            <div className="flex items-center justify-between px-6 py-4 border-b border-background-200">
              <h3 className="font-heading font-semibold text-lg text-foreground-900">
                {editingId ? 'Edit Record' : 'Add New Record'}
              </h3>
              <button
                type="button"
                onClick={closeModal}
                className="p-2 rounded-md hover:bg-background-100 cursor-pointer"
                aria-label="Close"
              >
                <i className="ri-close-line text-xl" aria-hidden="true" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
              {fields.map((f) => (
                <FieldInput
                  key={f.name}
                  field={f}
                  value={form[f.name]}
                  options={f.type === 'category' ? distinctOptions(f.name) : undefined}
                  onChange={(v) => setForm((p) => ({ ...p, [f.name]: v }))}
                />
              ))}
            </div>

            <div className="px-6 py-4 border-t border-background-200 flex justify-end gap-3">
              <button type="button" onClick={closeModal} className="btn-outline whitespace-nowrap">
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="btn-primary whitespace-nowrap"
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setConfirmDelete(null)} aria-hidden="true" />
          <div className="relative bg-white rounded-xl w-full max-w-sm p-6 shadow-2xl">
            <h3 className="font-heading font-semibold text-lg text-foreground-900">Delete this record?</h3>
            <p className="mt-2 text-sm text-foreground-500">
              This action cannot be undone. Are you sure you want to continue?
            </p>
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setConfirmDelete(null)}
                className="btn-outline whitespace-nowrap"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDelete}
                disabled={busy}
                className="px-4 py-2.5 rounded-md bg-red-500 text-white text-xs font-label font-medium uppercase tracking-wide hover:bg-red-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Cell({ column, value }: { column: ColumnConfig; value: unknown }) {
  if (column.type === 'image') {
    const src = typeof value === 'string' ? value : '';
    return src ? (
      <img src={src} alt="" className="w-12 h-12 rounded-md object-cover object-top" />
    ) : (
      <span className="text-foreground-300 text-xs">—</span>
    );
  }
  if (column.type === 'boolean') {
    return value ? (
      <span className="inline-flex items-center gap-1 text-accent-600 text-xs font-medium">
        <i className="ri-checkbox-circle-fill" aria-hidden="true" /> Published
      </span>
    ) : (
      <span className="inline-flex items-center gap-1 text-foreground-400 text-xs">
        <i className="ri-indeterminate-circle-line" aria-hidden="true" /> Draft
      </span>
    );
  }
  if (column.type === 'date') {
    return <span className="text-foreground-600">{String(value ?? '').slice(0, 10)}</span>;
  }
  const text = String(value ?? '');
  return (
    <span className="text-foreground-700 line-clamp-2 max-w-[240px] block" title={text}>
      {text || <span className="text-foreground-300">—</span>}
    </span>
  );
}

function FieldInput({
  field,
  value,
  onChange,
  options,
}: {
  field: FieldConfig;
  value: string | number | boolean | string[];
  onChange: (v: string | number | boolean | string[]) => void;
  options?: string[];
}) {
  const baseClass =
    'w-full px-3.5 py-2.5 rounded-md text-sm border border-background-300 bg-white outline-none focus:ring-2 focus:ring-primary-300 text-foreground-900';

  return (
    <div>
      <label className="block text-xs font-label font-medium text-foreground-700 mb-1.5">
        {field.label}
        {field.required && <span className="text-red-500 ml-0.5">*</span>}
      </label>

      {field.type === 'textarea' ? (
        <textarea
          value={String(value)}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder}
          required={field.required}
          rows={4}
          className={`${baseClass} resize-y`}
        />
      ) : field.type === 'checkbox' ? (
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={Boolean(value)}
            onChange={(e) => onChange(e.target.checked)}
            className="w-4 h-4 accent-primary-500"
          />
          <span className="text-sm text-foreground-600">Enabled / Published</span>
        </label>
      ) : field.type === 'category' ? (
        <CategorySelect
          value={String(value)}
          options={options ?? []}
          onChange={(v) => onChange(v)}
        />
      ) : field.type === 'select' ? (
        <select
          value={String(value)}
          onChange={(e) => onChange(e.target.value)}
          required={field.required}
          className={baseClass}
        >
          <option value="">Select...</option>
          {field.options?.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      ) : field.type === 'image' ? (
        <ImageUpload value={String(value)} onChange={(url) => onChange(url)} />
      ) : field.type === 'images' ? (
        <MultiImageUpload value={Array.isArray(value) ? value : []} onChange={(urls) => onChange(urls)} />
      ) : (
        <input
          type={field.type === 'number' ? 'number' : field.type === 'url' ? 'url' : field.type === 'date' ? 'date' : field.type === 'time' ? 'time' : 'text'}
          value={field.type === 'number' ? Number(value) : String(value)}
          onChange={(e) => onChange(field.type === 'number' ? Number(e.target.value) : e.target.value)}
          placeholder={field.placeholder}
          required={field.required}
          className={baseClass}
        />
      )}

      {field.help && <p className="mt-1 text-xs text-foreground-400">{field.help}</p>}
    </div>
  );
}

function CategorySelect({
  value,
  options,
  onChange,
}: {
  value: string;
  options: string[];
  onChange: (v: string) => void;
}) {
  const [adding, setAdding] = useState(false);
  const [custom, setCustom] = useState('');
  const isCustomValue = value !== '' && !options.includes(value);
  const selectValue = adding ? '__add__' : value;

  const selectClass =
    'w-full px-3.5 py-2.5 rounded-md text-sm border border-background-300 bg-white outline-none focus:ring-2 focus:ring-primary-300 text-foreground-900';

  return (
    <div className="space-y-2">
      <select
        value={selectValue}
        onChange={(e) => {
          if (e.target.value === '__add__') {
            setAdding(true);
            setCustom('');
            return;
          }
          setAdding(false);
          onChange(e.target.value);
        }}
        className={selectClass}
      >
        <option value="">Select a category...</option>
        {isCustomValue && <option value={value}>{value}</option>}
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
        <option value="__add__">＋ Add new category…</option>
      </select>

      {adding && (
        <div className="flex gap-2">
          <input
            type="text"
            value={custom}
            onChange={(e) => setCustom(e.target.value)}
            placeholder="New category name"
            className="flex-1 px-3.5 py-2.5 rounded-md text-sm border border-background-300 bg-white outline-none focus:ring-2 focus:ring-primary-300 text-foreground-900"
          />
          <button
            type="button"
            onClick={() => {
              const trimmed = custom.trim();
              if (!trimmed) return;
              onChange(trimmed);
              setAdding(false);
              setCustom('');
            }}
            className="px-4 py-2.5 rounded-md bg-primary-500 text-white text-xs font-label font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            Add
          </button>
        </div>
      )}
    </div>
  );
}